import React, { useCallback } from "react";
import {
    FinancialAccount,
    FinancialAccountSourceType,
} from "@common/types/financialAccount";
import { FinancialConnection } from "@common/types/financialConnection";
import { StripeConnectionKind } from "@common/types/stripeConnection";
import {
    isIncompleteStripeConnection,
    isReadyStripeExpressConnection,
} from "@common/helpers/stripeConnection";
import { StripeExpressReturnTo } from "@common/schemas/domains/stripe/stripe-express.schema";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { Entity } from "@common/types/entity";
import { Field } from "@design-system/Field/Field";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { PlusIcon } from "@icons";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useStripeExpressRedirect } from "@hooks/useStripeExpressRedirect";
import { useUserIntegrations } from "@hooks/useUserIntegrations";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import {
    useCreateStripeExpressMutation,
    useRemintStripeExpressMutation,
} from "@mutations/stripeConnection";
import { useNativeInvoiceFormContext } from "./NativeInvoiceFormContext";
import styles from "./NativeInvoiceCollectPaymentField.module.scss";
import {
    findPaymentMethodOption,
    NATIVE_INVOICE_PAYMENT_METHOD_OPTIONS,
    parsePaymentMethodPickerValue,
    paymentMethodPickerValue,
} from "./nativeInvoicePaymentMethodOptions";

const COLLECT_PAYMENT_OFF = "off";
const CONNECT_STRIPE = "connect-stripe";
const FINISH_SETUP_PREFIX = "finish-setup:";

interface Props {
    selectedEntity?: Entity;
    invoiceId?: string;
}

function expressConnectionForAccount(
    account: FinancialAccount,
): FinancialConnection | undefined {
    for (const integrationAccount of account.integrationAccounts ?? []) {
        const connection = integrationAccount.connection;
        if (connection && connection.kind === StripeConnectionKind.EXPRESS) {
            return connection;
        }
    }
    return undefined;
}

function stripeAccountLabel(account: {
    name: string | null;
    accountNumberMask: string | null;
}): string {
    const trimmedName = account.name?.trim() ?? "";
    const name = trimmedName.length > 0 ? trimmedName : "Stripe";
    if (account.accountNumberMask) {
        return `${name} ••${account.accountNumberMask}`;
    }
    return name;
}

export const NativeInvoiceCollectPaymentField: React.FC<Props> = ({
    selectedEntity,
    invoiceId,
}) => {
    const { state, setState } = useNativeInvoiceFormContext();
    const { hasAccess: hasPlanAccess, isFetched: isPlanFetched } =
        useAllowFeature(PREMIUM_FEATURES.INVOICE_PAYMENT_COLLECTION);
    // Free plans don't include payment collection, so the field is hidden. An
    // invoice that already collects keeps it visible so it can still be turned
    // off.
    const isCollectPaymentEnabled =
        state.collectPayment || (isPlanFetched && hasPlanAccess);
    const connectionsQuery = useUserIntegrations();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const redirectToStripe = useStripeExpressRedirect();
    const createExpressMutation = useCreateStripeExpressMutation();
    const remintMutation = useRemintStripeExpressMutation();

    const allStripeAccounts = useFinancialAccounts({
        entityIds: selectedEntity ? [selectedEntity.id] : [],
        sourceTypes: [FinancialAccountSourceType.STRIPE],
        onlyBusiness: true,
    }).filter((account) => !account.disabled);

    // Only Express accounts can collect payment. Ready = KYC complete
    // (chargesEnabled); Incomplete = Express but KYC not finished.
    const readyExpressAccounts = allStripeAccounts.filter((account) => {
        const connection = expressConnectionForAccount(account);
        return connection != null && isReadyStripeExpressConnection(connection);
    });
    const incompleteExpressAccounts = allStripeAccounts.flatMap((account) => {
        const connection = expressConnectionForAccount(account);
        return connection != null && isIncompleteStripeConnection(connection)
            ? [{ account, connectionId: connection.id }]
            : [];
    });

    // The currently persisted account stays selectable even if its connection is
    // no longer Express-ready, so editing an existing collect-payment invoice
    // doesn't silently drop its destination.
    const selectedAccount = allStripeAccounts.find(
        (account) => account.id === state.stripeFinancialAccountId,
    );
    const selectableAccounts =
        selectedAccount &&
        !readyExpressAccounts.some(
            (account) => account.id === selectedAccount.id,
        )
            ? [...readyExpressAccounts, selectedAccount]
            : readyExpressAccounts;

    const hasSelectable = selectableAccounts.length > 0;
    const showFinishSetup =
        !hasSelectable && incompleteExpressAccounts.length > 0;
    // Treat the connections query as settled on both success and failure
    // (isFetched) — otherwise a failed query would hide the connect button and
    // leave the user stuck.
    const canShowConnectStripe =
        !hasSelectable &&
        incompleteExpressAccounts.length === 0 &&
        (allStripeAccounts.length === 0 || connectionsQuery.isFetched);

    const handleConnectStripe = useCallback(async () => {
        if (!selectedEntity || refuseIfViewOnly()) {
            return;
        }
        await redirectToStripe(() =>
            createExpressMutation.mutateAsync({
                defaultEntityId: selectedEntity.id,
                returnTo: StripeExpressReturnTo.INVOICE,
                invoiceId,
            }),
        );
    }, [
        selectedEntity,
        refuseIfViewOnly,
        redirectToStripe,
        createExpressMutation,
        invoiceId,
    ]);

    const handleFinishSetup = useCallback(
        async (connectionId: number) => {
            if (refuseIfViewOnly()) {
                return;
            }
            await redirectToStripe(() =>
                remintMutation.mutateAsync({
                    connectionId,
                    returnTo: StripeExpressReturnTo.INVOICE,
                    invoiceId,
                }),
            );
        },
        [refuseIfViewOnly, redirectToStripe, remintMutation, invoiceId],
    );

    const handleValueChange = useCallback(
        (value: string | null) => {
            if (value === CONNECT_STRIPE) {
                void handleConnectStripe();
                return;
            }
            if (value?.startsWith(FINISH_SETUP_PREFIX)) {
                const connectionId = Number(
                    value.slice(FINISH_SETUP_PREFIX.length),
                );
                if (Number.isInteger(connectionId)) {
                    void handleFinishSetup(connectionId);
                }
                return;
            }
            if (value == null || value === COLLECT_PAYMENT_OFF) {
                setState((prev) => ({
                    ...prev,
                    collectPayment: false,
                    stripeFinancialAccountId: null,
                }));
                return;
            }
            const parsed = parsePaymentMethodPickerValue(value);
            if (!parsed) {
                return;
            }
            setState((prev) => ({
                ...prev,
                collectPayment: true,
                stripeFinancialAccountId: parsed.accountId,
                acceptedPaymentMethods: parsed.option.methods,
            }));
        },
        [handleConnectStripe, handleFinishSetup, setState],
    );

    if (!isCollectPaymentEnabled) {
        return null;
    }

    const selectedOption = findPaymentMethodOption(
        state.acceptedPaymentMethods,
    );
    const isAccountSelected = state.collectPayment && selectedAccount != null;
    const selectValue = isAccountSelected
        ? paymentMethodPickerValue({
              accountId: selectedAccount.id,
              option: selectedOption,
          })
        : COLLECT_PAYMENT_OFF;
    const selectedLabel = isAccountSelected ? (
        <>
            {stripeAccountLabel(selectedAccount)}{" "}
            <span className={styles.selectedMethods}>
                {selectedOption.label}
            </span>
        </>
    ) : (
        "None"
    );

    const items: Array<PickerItemData<string>> = [
        { value: COLLECT_PAYMENT_OFF, label: "None" },
        ...selectableAccounts.map((account) => ({
            value: String(account.id),
            label: stripeAccountLabel(account),
            children: NATIVE_INVOICE_PAYMENT_METHOD_OPTIONS.map((option) => ({
                value: paymentMethodPickerValue({
                    accountId: account.id,
                    option,
                }),
                label: option.label,
            })),
        })),
        ...(showFinishSetup
            ? incompleteExpressAccounts.map(({ account, connectionId }) => ({
                  value: `${FINISH_SETUP_PREFIX}${connectionId}`,
                  label: `${stripeAccountLabel(account)} — Finish setup`,
              }))
            : []),
        ...(canShowConnectStripe && selectedEntity
            ? [
                  {
                      value: CONNECT_STRIPE,
                      label: "Connect Stripe",
                      leading: <PlusIcon />,
                  },
              ]
            : []),
    ];

    return (
        <Field label="Payment account" control="button">
            <Picker.Select
                items={items}
                nested="flyout"
                value={selectValue}
                onValueChange={handleValueChange}
                placeholder="None"
                renderValue={() => selectedLabel}
                trigger={<Picker.Button variant="primary" size="xl" block />}
            />
        </Field>
    );
};
